import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

const emailRegExp = /^[A-Za-z0-9_+%-]+(\.[A-Za-z0-9_+%-]+)*@[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,24}$/;
const telegramUsernameRegExp = /^@?[A-Za-z0-9_]+$/;

const invalidPatterns = [' ', '..', '@-', '-@', '_@', '@_', '@.', '.@', '-.', '_.', '._'];

export const emailValidator = (): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (
      value &&
      (!emailRegExp.test(value) ||
        value.includes(' ') ||
        value.split('@').length !== 2 ||
        invalidPatterns.some(pattern => value.includes(pattern)))
    ) {
      return { emailInvalid: true };
    }
    return null;
  };
};

export const cardNumberValidator = (): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null;
    }

    const validCardLength = 16;
    const cleanPan = cleanStringFormSpaces(control.value);
    const isValid = cleanPan.length === validCardLength && luhnCheck(control.value);
    if (!isValid) {
      return { cardInvalid: true };
    }

    return null;
  };
};

/**
 * The `_cleanStringFormSpaces` method is used to clean a string by removing all spaces.
 *
 * @param {string} value - The string to be cleaned from spaces.
 * @returns {string} - Returns the cleaned string from spaces.
 */
const cleanStringFormSpaces = (value: string): string => {
  return value.replaceAll(' ', '');
};

/**
 * The `_luhnCheck` method is used to calculate card number by 'Luhn' algorithm.
 *
 * @param {string} pan - The card number string to be checked.
 * @returns {boolean} - Returns card number validation result.
 */
const luhnCheck = (pan: string): boolean => {
  const arr = [...pan].reverse().map(x => Number.parseInt(x));
  const lastDigit = arr.shift();
  let sum = arr.reduce((acc, val, i) => (i % 2 === 0 ? acc + ((val *= 2) > 9 ? val - 9 : val) : acc + val), 0);
  sum += lastDigit;

  return sum % 10 === 0;
};

export const limitIntegerDigits = (value: string, maxDigits = 12): string => {
  if (!value) return value;
  let normalizedValue = value.replace(/,/g, '.');

  if (normalizedValue.trim() === '.') {
    normalizedValue = '0.';
  }

  const match = normalizedValue.match(/[.]/);
  const sep = match ? match[0] : '';
  const [integer = '', decimals] = normalizedValue.split(/[.]/);

  const trimmedInt = integer.slice(0, maxDigits);

  return decimals !== undefined ? `${trimmedInt}${sep}${decimals}` : trimmedInt;
};

export const telegramUsernameValidator = (): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;

    if (value == null || value === '') {
      return null;
    }

    const trimmedValue = String(value).trim();

    if (!telegramUsernameRegExp.test(trimmedValue)) {
      return { telegramInvalidChars: true };
    }

    return null;
  };
};

export const telegramUsernameMaxLengthValidator = (maxLength: number): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;

    if (value == null || value === '') {
      return null;
    }

    const usernameLength = String(value).trim().replace(/^@/, '').length;

    if (usernameLength > maxLength) {
      return { maxlength: { requiredLength: maxLength, actualLength: usernameLength } };
    }

    return null;
  };
};

const phoneRegExp = new RegExp(String.raw`^(\+[\d\s().-]*|[\d\s().-]+)$`);

/**
 * Enum of restricted phone prefixes for blocked countries and territories.
 * Format: country code without '+' sign
 */
enum RestrictedPhonePrefix {
  Abkhazia = '7940',
  Afghanistan = '93',
  Bahamas = '1242',
  Belarus = '375',
  Benin = '229',
  Cameroon = '237',
  CentralAfricanRepublic = '236',
  Chad = '235',
  CongoDemocraticRepublic = '243',
  IvoryCoast = '225',
  Crimea = '7978',
  Cuba = '53',
  DonetskNationalRepublic = '7949',
  Eritrea = '291',
  GazaStripPalestineWestBank = '970',
  Ghana = '233',
  Guinea = '224',
  GuineaBissau = '245',
  Haiti = '509',
  Iran = '98',
  Iraq = '964',
  NorthKorea = '850',
  Kosovo = '383',
  Kuwait = '965',
  Lebanon = '961',
  Liberia = '231',
  Libya = '218',
  LuhanskNationalRepublic = '7959',
  Mali = '223',
  Myanmar = '95',
  Nicaragua = '505',
  Pakistan = '92',
  Panama = '507',
  Qatar = '974',
  Russia = '79',
  Somalia = '252',
  SouthOssetia = '7929',
  SouthSudan = '211',
  Sudan = '249',
  Syria = '963',
  Togo = '228',
  TrinidadAndTobago = '1868',
  Uganda = '256',
  Venezuela = '58',
  Yemen = '967',
  Zimbabwe = '263',
}

/**
 * Sorted prefixes from longest to shortest to ensure more specific matches take priority.
 * This prevents false positives where a shorter prefix might incorrectly match a longer code.
 */
const sortedRestrictedPrefixes: string[] = Object.values(RestrictedPhonePrefix).sort((a, b) => b.length - a.length);

/**
 * Checks if a phone number belongs to a restricted country/territory.
 *
 * @param {string} phone - The cleaned phone number (digits and + only).
 * @returns {boolean} - Returns true if the phone is from a restricted region.
 */
const isPhoneBlocked = (phone: string): boolean => {
  const phoneDigits = phone.startsWith('+') ? phone.slice(1) : phone;

  return sortedRestrictedPrefixes.some(prefix => phoneDigits.startsWith(prefix));
};

export const phoneValidator = (): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;

    if (!value) {
      return { phoneRequired: true };
    }

    const cleanedValue = cleanPhoneNumber(value);

    if (cleanedValue.length === 0) {
      return { phoneRequired: true };
    }

    if (!phoneRegExp.test(value)) {
      return { phoneInvalid: true };
    }

    if (isPhoneBlocked(cleanedValue)) {
      return { phoneBlocked: true };
    }

    return null;
  };
};

export const cleanPhoneNumber = (phone: string): string => {
  return phone.replaceAll(/[^0-9+]/g, '');
};
